import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '../../auto-unsubscribe.decorator';
import { EmitterEvent, EmitterService } from '../../emitter.service';
import { PopUpDialogParams } from '../dialog-service/dialog.service';

const EVENT_CHANNEL_NAME = 'PopUpDialogComponent';
export const CONFIRM_BTN_STATUS = 'CONFIRM_BTN_STATUS';
export const CONFIRM = 'CONFIRM';
export const CLOSE = 'CLOSE';

/**
 * Mat-Dialog Pop-Up Component
 * Universal pop-up component which can broadcast a message to the user or confirm a decision.
 */
@AutoUnsubscribe()
@Component({
  selector: 'app-pop-up-dialog',
  templateUrl: 'pop-up-dialog.component.html',
  styleUrls: ['pop-up-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, TranslatePipe],
})
export class PopUpDialogComponent implements OnInit {
  title: string;
  message: string;
  confirm: string;
  cancel: string;
  displayCancel: boolean;
  icon: string;
  templateRef: TemplateRef<unknown>;
  canConfirm: boolean;
  alwaysUnlockConfirm: boolean;
  closeOnConfirm: boolean;
  hidePopupLayoutElements: boolean;
  private readonly eventSub: Subscription;
  static channelName = (): string => EVENT_CHANNEL_NAME;

  constructor(
    private readonly dialogRef: MatDialogRef<PopUpDialogComponent>,
    @Inject(MAT_DIALOG_DATA) protected readonly data: PopUpDialogParams,
    private readonly translateService: TranslateService,
    private readonly emitterService: EmitterService,
  ) {
    this.eventSub = this.emitterService.get(PopUpDialogComponent.channelName()).subscribe((e: EmitterEvent) => this.eventReceived(e));
  }

  /**
   * Will check if each parameter exits in @param data
   * If none exists, will set default messages and values.
   */
  ngOnInit(): void {
    this.title = this.translateService.instant(this.data.title ?? 'uix.popUpDialog.title');
    this.message = this.translateService.instant(this.data.message ?? 'uix.popUpDialog.message', this.data.messageParams);
    this.icon = this.data.icon;
    this.confirm = this.translateService.instant(this.data.confirm ?? 'commons.btn.confirm');
    this.cancel = this.translateService.instant(this.data.cancel ?? 'commons.btn.cancel');
    this.displayCancel = this.data.displayCancel !== false;
    this.templateRef = this.data.templateRef;
    this.alwaysUnlockConfirm = this.data.alwaysUnlockConfirm;
    this.canConfirm = !this.templateRef || this.alwaysUnlockConfirm;
    this.closeOnConfirm = this.data.closeOnConfirm ?? true;
    this.hidePopupLayoutElements = this.data.hidePopupLayoutElements ?? false;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.emitterService.get(PopUpDialogComponent.channelName()).next({ event: CONFIRM, value: true });
    if (this.closeOnConfirm) {
      this.dialogRef.close(true);
    }
  }

  eventReceived(e: EmitterEvent): void {
    if (e.event === CONFIRM_BTN_STATUS) {
      this.canConfirm = e.value;
    } else if (e.event === CLOSE) {
      this.dialogRef.close(true);
    }
  }
}
